import type { DefineComponent } from 'vue';
import type { PageProps } from './types/page';
import { createInertiaApp, Link, router } from '@inertiajs/vue3';
import messages from '@intlify/unplugin-vue-i18n/messages';
import * as sentry from '@sentry/vue';
import { toast } from '@youcan/celeste';

import { createPinia } from 'pinia';
import { createApp, h } from 'vue';
import Popper from 'vue3-popper';
import { createI18n } from 'vue-i18n';
import { ZiggyVue } from 'ziggy-js';
import MainLayout from '@/components/layout/main-layout.vue';
import './shims';

// YouCan UI
import 'uno.css';
import 'vue-sonner/style.css';
import '@youcan/celeste-tokens/tokens.css';
import '@youcan/celeste/assets/celeste.css';
import '@/assets/css/main.css';

createInertiaApp({
  id: 'app',
  progress: { color: 'var(--color-primary-base)' },

  title: (title: string) => title ? `${title} — YouCan` : 'YouCan',

  layout: () => MainLayout,

  resolve: (name) => {
    const pages = import.meta.glob(['./pages/**/*.vue', '!./pages/**/_*/**']);
    return pages[`./pages/${name}.vue`]() as Promise<DefineComponent>;
  },

  defaults: {
    visitOptions: () => {
      return { viewTransition: true };
    },
  },

  http: {
    xsrfCookieName: 'XSRF-TOKEN-ACCOUNTS',
  },

  setup({ App, el, props, plugin }) {
    const proops = props.initialPage.props as unknown as PageProps;

    const app = createApp({ render: () => h(App, props) });

    sentry.init({
      app,
      sendDefaultPii: true,
      tracesSampleRate: 0.1,
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      integrations: [
        sentry.browserTracingIntegration(),
        sentry.replayIntegration({
          blockAllMedia: true,
          maskAllText: false,
          maskAllInputs: false,
        }),
        sentry.thirdPartyErrorFilterIntegration({
          filterKeys: ['YOUCAN'],
          behaviour: 'drop-error-if-contains-third-party-frames',
        }),
      ],
      dsn: 'https://a72b310516f93271275b5392fb0b758c@t.youcan.shop/2',
    });

    const i18n = createI18n({
      messages,
      legacy: false,
      locale: proops.locale,
    });

    app.use(plugin);
    app.use(createPinia());
    app.use(ZiggyVue);
    app.use(i18n);

    app.component('Popper', Popper);
    app.component('RouterLink', Link);

    app.provide('ycid', window._yc_id);

    if (proops.user) {
      window.gtag?.('set', { user_id: proops.user.id });
      sentry.setUser({ email: proops.user.email, id: proops.user.id });
    }

    // router.on('before', (event) => {
    //   event.detail.visit.headers['X-XSRF-TOKEN'] = decodeURIComponent(cookie('XSRF-TOKEN-ACCOUNTS') ?? '');
    // });

    router.on('networkError', (event) => {
      event.preventDefault();

      toast.error(i18n.global.t('common.toast.error'), {
        description: event.detail.error.message,
      });
    });

    app.mount(el);
  },
});
